import {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { type PaddleEventData } from "@paddle/paddle-js";
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collection } from "../components/collection";
import { useCart } from "../contexts/cart-context";
import usePaddle, { eventCallback } from "@/contexts/paddle/paddle-context";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect } from "react";

export const CartOverlay = () => {
  const { cart, total, clear } = useCart();
  const paddle = usePaddle();
  const cartCount = cart.length;

  useEffect(() => {
    paddle.Update({
      eventCallback: (eventData: PaddleEventData) => {
        eventCallback(eventData);

        if (eventData.name === "checkout.completed") {
          console.log("clearing cart");
          clear();
        }
      },
    });
  }, [paddle]);

  const handleCheckout = async () => {
    let custom_data: { is_marketplace: boolean; environment?: string } = { is_marketplace: true };
    if (import.meta.env.VITE_REACT_APP_PADDLE_ENVIRONMENT == "sandbox") {
      custom_data = { ...custom_data, environment: import.meta.env.VITE_REACT_APP_ENV };
    }

    paddle?.Checkout.open({
      items: cart.map((item) => (
        { quantity: 1, priceId: item.cartItem?.salePriceActive! ? item.cartItem?.salePriceId! : item.cartItem?.priceId! }
      )),
      customData: custom_data,
    })
  };

  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <Button className="flex align-middle bg-musio-green text-musio-mid-gray hover:bg-musio-green active:bg-musio-green">
            <ShoppingCart />
            <span>
              Cart
              <span className="hidden sm:inline-block">
                · {cartCount} Item(s)
              </span>
            </span>
          </Button>
        </DialogTrigger>

        <DialogPortal>
          <DialogOverlay className="AlertDialogOverlay" />
          <DialogContent className="AlertDialogContent flex flex-col space-y-2 min-h-0 overflow-hidden p-2 sm:p-0">
            <DialogTitle className="AlertDialogTitle p-8 flex flex-row justify-between bg-musio-bg items-center">
              <div className="flex flex-col space-y-1">
                <p className="text-[30px] font-semibold text-musio-light-lilac">
                  Cart
                </p>
                <p className="text-sm text-lilac">Review your items</p>
              </div>

              <DialogClose asChild>
                <Button
                  className="select-none w-full max-w-[179px] h-[35px] flex items-center justify-center rounded-2xl font-bold bg-musio-blue text-musio-light-lilac cursor-pointer hover:bg-musio-blue/90"
                >
                  <p className="text-sm">Continue Shopping</p>
                </Button>
              </DialogClose>
            </DialogTitle>

            <ScrollArea className="flex-1 min-h-0 bg-musio-bg" type="hover">
              <DialogDescription asChild>
                <div className="AlertDialogDescription p-8">
                  {cart.length === 0 ? (
                    <div className="text-musio-dark-lilac">No Items in cart</div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {cart.map((item) => (
                        <Collection
                          key={item.collectionId}
                          item={item}
                          inCart
                          variant="in-cart"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </DialogDescription>
            </ScrollArea>

            <div className="p-8 w-full bg-musio-bg">
              <div className="flex flex-col space-y-2 items-center">
                <p className="text-sm text-musio-text-light">Subtotal: {total}</p>

                <DialogClose asChild>
                  <Button
                    disabled={cart.length === 0}
                    className={`
                    select-none
                    w-full
                    max-w-[404px]
                    h-[35px]
                    flex
                    items-center
                    justify-center
                    rounded-2xl
                    font-bold
                    text-musio-light-lilac
                    cursor-pointer
                    disabled:bg-musio-green
                    ${cart.length === 0 ? "bg-grey" : "bg-musio-green"}
                  `}
                    onClick={() => handleCheckout()}
                  >
                    <p className="text-sm text-musio-mid-gray">Checkout</p>
                  </Button>
                </DialogClose>
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <span className="text-musio-text-light">
        <span className="hidden sm:inline-block">
          {total.length !== 0 && "Subtotal:"}
        </span>{" "}
        {total}
      </span>
    </>
  );

}
